import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { vehiclesAPI, customersAPI } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogFooter,
  DialogDescription
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  Car,
  User,
  Gauge,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

const ITEMS_PER_PAGE = 12;

export default function VehiclesPage() {
  const [searchParams] = useSearchParams();
  const customerId = searchParams.get('customer');
  
  const [vehicles, setVehicles] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [formData, setFormData] = useState({
    customer_id: '',
    vehicle_number: '',
    chassis_number: '',
    meter_reading: '',
    color: '',
    make: '',
    model: '',
    year: '',
    engine_type: '',
    notes: ''
  });
  const [saving, setSaving] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const fetchData = async () => {
    try {
      const [vehiclesRes, customersRes] = await Promise.all([
        vehiclesAPI.getAll({ search: search || undefined, customer_id: customerId || undefined }),
        customersAPI.getAll()
      ]);
      setVehicles(vehiclesRes.data);
      setCustomers(customersRes.data);
      setCurrentPage(1);
    } catch (error) {
      toast.error('Failed to load vehicles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, customerId]);

  const handleOpenModal = (vehicle = null) => {
    if (vehicle) {
      setEditingVehicle(vehicle);
      setFormData({
        customer_id: vehicle.customer_id,
        vehicle_number: vehicle.vehicle_number,
        chassis_number: vehicle.chassis_number || '',
        meter_reading: vehicle.meter_reading?.toString() || '',
        color: vehicle.color || '',
        make: vehicle.make || '',
        model: vehicle.model || '',
        year: vehicle.year?.toString() || '',
        engine_type: vehicle.engine_type || '',
        notes: vehicle.notes || ''
      });
    } else {
      setEditingVehicle(null);
      setFormData({
        customer_id: customerId || '',
        vehicle_number: '',
        chassis_number: '',
        meter_reading: '',
        color: '',
        make: '',
        model: '',
        year: '',
        engine_type: '',
        notes: ''
      });
    }
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    const payload = {
      ...formData,
      meter_reading: formData.meter_reading ? parseInt(formData.meter_reading) : null,
      year: formData.year ? parseInt(formData.year) : null
    };
    
    try {
      if (editingVehicle) {
        await vehiclesAPI.update(editingVehicle.id, payload);
        toast.success('Vehicle updated');
      } else {
        await vehiclesAPI.create(payload);
        toast.success('Vehicle created');
      }
      setShowModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save vehicle');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (vehicle) => {
    if (!window.confirm(`Delete vehicle ${vehicle.vehicle_number}?`)) return;
    try {
      await vehiclesAPI.delete(vehicle.id);
      toast.success('Vehicle deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete vehicle');
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="vehicles-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black font-heading tracking-tight">Vehicles</h1>
          <p className="text-muted-foreground mt-1">Manage customer vehicles</p>
        </div>
        <Button onClick={() => handleOpenModal()} className="font-semibold uppercase tracking-wide" data-testid="add-vehicle-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Vehicle
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Search by vehicle number..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
          data-testid="vehicle-search-input"
        />
      </div>

      {/* Vehicles Grid */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="spinner" />
        </div>
      ) : vehicles.length > 0 ? (
      <>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {vehicles.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE).map((vehicle) => (
            <Card key={vehicle.id} className="card-hover" data-testid={`vehicle-card-${vehicle.id}`}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center">
                      <Car className="w-6 h-6 text-slate-600" />
                    </div>
                    <div>
                      <h3 className="font-mono font-bold text-lg">{vehicle.vehicle_number}</h3>
                      {vehicle.make && vehicle.model && (
                        <p className="text-sm text-muted-foreground">
                          {vehicle.make} {vehicle.model} {vehicle.year && `(${vehicle.year})`}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      onClick={() => handleOpenModal(vehicle)}
                      data-testid={`edit-vehicle-${vehicle.id}`}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      onClick={() => handleDelete(vehicle)}
                      className="text-red-500 hover:text-red-600 hover:bg-red-50"
                      data-testid={`delete-vehicle-${vehicle.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {vehicle.customer && (
                  <div className="flex items-center gap-2 mt-4 text-sm">
                    <User className="w-4 h-4 text-muted-foreground" />
                    <span>{vehicle.customer.name}</span>
                    <span className="text-muted-foreground">• {vehicle.customer.phone}</span>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-2 mt-4 text-sm">
                  {vehicle.color && (
                    <div>
                      <span className="text-muted-foreground">Color:</span> {vehicle.color}
                    </div>
                  )}
                  {vehicle.meter_reading && (
                    <div className="flex items-center gap-1">
                      <Gauge className="w-4 h-4 text-muted-foreground" />
                      {vehicle.meter_reading.toLocaleString()} km
                    </div>
                  )}
                </div>

                {vehicle.chassis_number && (
                  <div className="mt-3 text-xs font-mono text-muted-foreground">
                    Chassis: {vehicle.chassis_number}
                  </div>
                )}

                <Link to={`/vehicles/${vehicle.id}`} className="block mt-4">
                  <Button variant="outline" className="w-full" size="sm" data-testid={`view-vehicle-${vehicle.id}`}>
                    View Service History
                  </Button>
                </Link>
              </CardContent>
            </Card>
          ))}
        </div>
        {/* Pagination */}
        {Math.ceil(vehicles.length / ITEMS_PER_PAGE) > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1} to {Math.min(currentPage * ITEMS_PER_PAGE, vehicles.length)} of {vehicles.length}
            </p>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} data-testid="vehicles-prev-page">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm px-2">Page {currentPage} of {Math.ceil(vehicles.length / ITEMS_PER_PAGE)}</span>
              <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(Math.ceil(vehicles.length / ITEMS_PER_PAGE), p + 1))} disabled={currentPage === Math.ceil(vehicles.length / ITEMS_PER_PAGE)} data-testid="vehicles-next-page">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </>
      ) : (
        <div className="empty-state">
          <Car className="w-16 h-16 mx-auto text-muted-foreground/50" />
          <p className="mt-4 text-lg">No vehicles found</p>
          <Button onClick={() => handleOpenModal()} className="mt-4">
            Add Your First Vehicle
          </Button>
        </div>
      )}

      {/* Add/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg" data-testid="vehicle-modal">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingVehicle ? 'Edit Vehicle' : 'Add Vehicle'}
            </DialogTitle>
            <DialogDescription>
              {editingVehicle ? 'Update vehicle information' : 'Add a new vehicle to the system'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="customer">Customer *</Label>
              <Select 
                value={formData.customer_id} 
                onValueChange={(value) => setFormData({ ...formData, customer_id: value })}
                required
              >
                <SelectTrigger data-testid="vehicle-customer-select">
                  <SelectValue placeholder="Select customer" />
                </SelectTrigger>
                <SelectContent>
                  {customers.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name} - {c.phone}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="vehicle_number">Vehicle Number *</Label>
                <Input
                  id="vehicle_number"
                  value={formData.vehicle_number}
                  onChange={(e) => setFormData({ ...formData, vehicle_number: e.target.value.toUpperCase() })}
                  placeholder="ABC-1234"
                  required
                  data-testid="vehicle-number-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="chassis_number">Chassis Number</Label>
                <Input
                  id="chassis_number"
                  value={formData.chassis_number}
                  onChange={(e) => setFormData({ ...formData, chassis_number: e.target.value.toUpperCase() })}
                  data-testid="vehicle-chassis-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="make">Make</Label>
                <Input
                  id="make"
                  value={formData.make}
                  onChange={(e) => setFormData({ ...formData, make: e.target.value.toUpperCase() })}
                  placeholder="TOYOTA"
                  data-testid="vehicle-make-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="model">Model</Label>
                <Input
                  id="model"
                  value={formData.model}
                  onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                  placeholder="Camry"
                  data-testid="vehicle-model-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="year">Year</Label>
                <Input
                  id="year"
                  type="number"
                  value={formData.year}
                  onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                  placeholder="2020"
                  data-testid="vehicle-year-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="color">Color</Label>
                <Input
                  id="color"
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  placeholder="Silver"
                  data-testid="vehicle-color-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="meter_reading">Meter Reading (km)</Label>
                <Input
                  id="meter_reading"
                  type="number"
                  value={formData.meter_reading}
                  onChange={(e) => setFormData({ ...formData, meter_reading: e.target.value })}
                  placeholder="50000"
                  data-testid="vehicle-meter-input"
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving} data-testid="save-vehicle-btn">
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
